/**
 * Scheduled replies — the client half of /api/ta-action-board/scheduled-sends.
 *
 * Always the signed-in recruiter's own: the proxy scopes every route to the verified Firebase
 * identity, so there is nothing to pass and no way to read a colleague's queue.
 *
 * The reply TEXT never travels back from the server — the strip needs a time, a name and a status,
 * and the editor already holds the text the recruiter just wrote. So there is no `body` on
 * `ScheduledSend` and nothing here to keep out of a log.
 *
 * PII note for callers: `candidateName` names a candidate (the proxy reads it off the message
 * itself). It belongs on screen and in Firestore, NEVER in an analytics event.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board/scheduled-sends`;

export type ScheduledSendStatus =
  | 'pending'
  /** The worker holds the lease and is sending right now — no longer cancellable. */
  | 'sending'
  | 'sent'
  | 'cancelled'
  /** The candidate wrote again before the send time, so nothing was sent. */
  | 'superseded'
  | 'failed';

export interface ScheduledSend {
  id: string;
  /**
   * 'reply' answers a candidate thread on Requests; 'rejection' is a fresh archive email on
   * Feedback. Only replies carry a thread — a rejection has no `threadId`/`targetMessageId`, and
   * instead names its Lever `opportunityId` and its `subject`. Absent on rows written before kinds
   * existed → read as 'reply'.
   */
  kind?: 'reply' | 'rejection';
  /** Only on a rejection: the candidate's Lever opportunity this email belongs to. */
  opportunityId?: string | null;
  /** Only on a rejection: the email's subject line (replies reuse the thread's). */
  subject?: string | null;
  /** Only on a reply: the Gmail thread the reply goes back into. */
  threadId?: string;
  /** Only on a reply: the candidate message it answers — the key the queue is decorated by. */
  targetMessageId?: string;
  candidateName: string | null;
  /** ISO instant. The five-minute tick means the real send is this time or up to 5 min later. */
  sendAt: string;
  status: ScheduledSendStatus;
  createdAt: string;
  updatedAt: string;
  /** Short code (`grant_revoked`, `too_late`, `missing_scope`, …) — never a raw error message. */
  lastErrorCode: string | null;
  terminalNote: string | null;
  /**
   * Set while a DUE row is being held back rather than sent (`pilot_locked`). Still `pending`, still
   * cancellable — but no longer "queued": the strip says so, because a held reply that looks queued
   * is how one sat unsent all morning. Absent on a proxy that predates the field, which reads as
   * not held.
   */
  heldCode?: string | null;
  heldAt?: string | null;
}

/** The recruiter has no permanent Gmail connection, so nothing could be sent later. */
export class NeedsGmailGrant extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NeedsGmailGrant';
  }
}

/** The worker is sending it as we speak — too late to stop. */
export class SendInFlight extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SendInFlight';
  }
}

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body?.error || fallback;
  } catch {
    return fallback;
  }
}

export async function fetchScheduledSends(): Promise<ScheduledSend[]> {
  const res = await authenticatedFetch(BASE);
  if (!res.ok) throw new Error(await readError(res, 'Could not load your scheduled replies'));
  const data = (await res.json()) as { items: ScheduledSend[] };
  return data.items || [];
}

/**
 * Queue a reply. `sendAt` is an absolute ISO instant — the caller converts the picker's wall-clock
 * value with `berlinWallClockToIso`, so "09:00" means 09:00 in Berlin and not in the browser's zone.
 *
 * The proxy resolves WHICH message is being answered from the thread itself, so there is nothing to
 * pass for that: a client-named message id was a real hole on the send path once.
 */
export async function scheduleReply(input: {
  threadId: string;
  /** The candidate's Lever opportunity — see `sendRequestReply`; the posting gate is judged from it. */
  opportunityId?: string | null;
  sendAt: string;
  body: string;
  gmailToken: string;
}): Promise<ScheduledSend> {
  const res = await authenticatedFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Gmail-Token': input.gmailToken },
    body: JSON.stringify({
      threadId: input.threadId,
      opportunityId: input.opportunityId ?? null,
      sendAt: input.sendAt,
      body: input.body,
    }),
    suppressErrorStatuses: [409],
  });
  if (res.status === 409) {
    throw new NeedsGmailGrant(await readError(res, 'Scheduling needs the permanent Gmail connection.'));
  }
  if (!res.ok) throw new Error(await readError(res, 'Could not schedule the reply'));
  const data = (await res.json()) as { scheduled: ScheduledSend };
  return data.scheduled;
}

/**
 * Move a queued reply to a different time. TIME ONLY — the text and the answered message are
 * untouched, so this needs no Gmail token and does not re-send the reply body.
 */
export async function rescheduleSend(id: string, sendAt: string): Promise<ScheduledSend> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sendAt }),
    // 423: the pilot's posting gate holds this row — an explanation for the modal, not an incident.
    suppressErrorStatuses: [404, 409, 423],
  });
  if (res.status === 404) throw new Error('That scheduled reply no longer exists — reload the tab.');
  if (res.status === 409) {
    throw new SendInFlight(await readError(res, 'This reply is being sent right now.'));
  }
  if (res.status === 423) throw new Error(await readError(res, 'Paused for the pilot test.'));
  if (!res.ok) throw new Error(await readError(res, 'Could not move the scheduled reply'));
  const data = (await res.json()) as { scheduled: ScheduledSend };
  return data.scheduled;
}

export async function cancelScheduledSend(id: string): Promise<ScheduledSend | null> {
  const res = await authenticatedFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    suppressErrorStatuses: [404, 409],
  });
  // Already gone is the outcome the recruiter wanted; nothing to report.
  if (res.status === 404) return null;
  if (res.status === 409) {
    throw new SendInFlight(await readError(res, 'This reply is being sent right now.'));
  }
  if (!res.ok) throw new Error(await readError(res, 'Could not cancel the scheduled reply'));
  const data = (await res.json()) as { scheduled: ScheduledSend };
  return data.scheduled;
}
